import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { LancamentoFinanceiro } from '../../extrato-movimentacoes.component';
import { AuthService } from '../../../../../shared/services/apis/usuario/auth.service';
import { PessoaLookupService } from '../../../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { ListPessoaIdsRequest } from '../../../../../shared/services/requests/pessoa/list-pessoa-ids-request';
import { CpfCnpjPipe } from '../../../../../shared/pipes/cpf-cnpj.pipe';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { ValorPorExtensoUtil } from '../../../../../shared/utils/valor-por-extenso.util';
import { NaturezaMovimentacaoEnum } from '../../../../../shared/enums/financas/natureza-movimentacao.enum';

export interface ImpressaoRecibosData {
    lancamentos: LancamentoFinanceiro[];
}

const MESES = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

@Component({
    selector: 'app-impressao-recibos',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
    ],
    providers: [AbntPipe],
    templateUrl: './impressao-recibos.component.html',
    styleUrls: ['./impressao-recibos.component.css']
})
export class ImpressaoRecibosComponent implements OnInit {

    public readonly NaturezaMovimentacaoEnum = NaturezaMovimentacaoEnum;
    public readonly dataEmissao = new Date();

    public nomeEmpresa: string = '';
    public cnpjEmpresaFormatado: string = '';
    public documentosPorIdPessoa = new Map<number, string>();
    public nomesPorIdPessoa = new Map<number, string>();

    private readonly cnpjPipe = new CpfCnpjPipe();

    constructor(
        public dialogRef: MatDialogRef<ImpressaoRecibosComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ImpressaoRecibosData,
        private authService: AuthService,
        private pessoaLookupService: PessoaLookupService,
        private abntPipe: AbntPipe,
    ) { }

    ngOnInit(): void {
        const userInfos = this.authService.getUserInfos();
        this.nomeEmpresa = userInfos.nomeEmpresa;
        this.cnpjEmpresaFormatado = this.cnpjPipe.transform(userInfos.cnpj);
        this.carregarDocumentosPessoas();
    }

    private carregarDocumentosPessoas(): void {
        const idsPessoa = [...new Set(
            this.data.lancamentos
                .map(l => l.idPessoa)
                .filter((id): id is number => !!id)
        )];

        if (!idsPessoa.length) return;

        const request = new ListPessoaIdsRequest();
        request.listIdPessoa = idsPessoa;

        this.pessoaLookupService.getListPessoaIds(request).subscribe({
            next: response => {
                if (response.isError) return;
                (response.listaPessoa || []).forEach(pessoa => {
                    const documento = pessoa.cnpj ? this.cnpjPipe.transform(pessoa.cnpj) : (pessoa.cpf || '');
                    this.documentosPorIdPessoa.set(pessoa.idPessoa, documento);
                    this.nomesPorIdPessoa.set(pessoa.idPessoa, pessoa.nome);
                });
            },
        });
    }

    public getDocumentoPessoa(idPessoa?: number): string {
        return idPessoa ? this.documentosPorIdPessoa.get(idPessoa) || '' : '';
    }

    public getNomePessoa(lancamento: LancamentoFinanceiro): string {
        if (lancamento.idPessoa) {
            return this.nomesPorIdPessoa.get(lancamento.idPessoa) || lancamento.cliente || '';
        }
        return lancamento.cliente || '';
    }

    public getNomeEDocumentoPessoa(lancamento: LancamentoFinanceiro): string {
        const nome = this.getNomePessoa(lancamento);
        const documento = this.getDocumentoPessoa(lancamento.idPessoa);
        return documento ? `${nome}, ${documento}` : nome;
    }

    public getValorPorExtenso(valor: number): string {
        return ValorPorExtensoUtil.converter(valor);
    }

    public getDataPorExtenso(data: Date): string {
        return `${data.getDate()} de ${MESES[data.getMonth()]} de ${data.getFullYear()}`;
    }

    public fechar(): void {
        this.dialogRef.close();
    }

    public imprimir(): void {
        const conteudo = document.querySelector('.recibos-print-container');
        if (!conteudo) return;

        const janelaImpressao = window.open('', '', 'height=900,width=800');
        if (!janelaImpressao) return;

        const styles = Array.from(document.styleSheets)
            .map(sheet => {
                try {
                    return Array.from(sheet.cssRules).map(r => r.cssText).join('');
                } catch { return ''; }
            })
            .join('');

        janelaImpressao.document.write(`
            <html>
                <head>
                    <title>Recibos de Lançamentos</title>
                    <base href="/">
                    <style>
                        ${styles}
                        *, *::before, *::after { box-sizing: border-box; }
                        html, body {
                            height: auto !important;
                            min-height: 0 !important;
                            margin: 0;
                            padding: 0;
                            font-family: Roboto, "Helvetica Neue", sans-serif;
                            color: #000;
                            background: #fff;
                        }
                        .recibos-print-container {
                            display: block !important;
                        }
                        .recibo-card {
                            page-break-after: always;
                            page-break-inside: avoid;
                            border: 1px solid #999 !important;
                            background: #fff !important;
                            color: #000 !important;
                            margin: 24px auto !important;
                            max-width: 640px !important;
                            box-shadow: none !important;
                        }
                        .recibo-card:last-child { page-break-after: auto; }
                        .no-print { display: none !important; }
                    </style>
                </head>
                <body>
                    ${conteudo.innerHTML}
                </body>
            </html>
        `);

        janelaImpressao.document.close();
        janelaImpressao.focus();
        setTimeout(() => {
            janelaImpressao.print();
            janelaImpressao.close();
        }, 600);
    }

    public formatarValor(valor: number): string {
        return this.abntPipe.transform(valor, 'preco', 2);
    }
}
